'use client';

import { c as _c } from "react/compiler-runtime";
import { animate, type AnimationPlaybackControls, useMotionValue } from 'framer-motion';
import { useEffect, useEffectEvent, useRef, useState } from 'react';
import { useReducedMotion } from '@/hooks/useReducedMotion';

//* RouletteItemStrip의 w-132·gap-12·px-20과 짝이다 — 클래스를 바꾸면 같이 바꾼다.
const ITEM_WIDTH = 132;
const ITEM_PITCH = ITEM_WIDTH + 12;
const STRIP_PADDING = 20;
const MAX_VIEWPORT_WIDTH = 750;
const IDLE_SPEED_PX_PER_SECOND = 40;
const SPIN_SPEED_PX_PER_SECOND = 1_800;
const RAMP_SECONDS = 0.6;
//* 가속(t³)의 끝 속도와 감속(ease-out cubic)의 첫 속도가 모두 평균의 3배라 이음매에서 속도가 튀지 않는다.
const RAMP_DISTANCE = SPIN_SPEED_PX_PER_SECOND * RAMP_SECONDS / 3;
const EASE_OUT_CUBIC = [0.33, 1, 0.68, 1] as const;
const MIN_LANDING_DISTANCE = 1_600;
const MIN_SPIN_MS = 1_600;
const LANDING_HOLD_MS = 700;

//* 띠를 여러 벌 이어 붙여 무한 회전처럼 보이게 한다. 응답이 빨라도 최소 회전 시간을 채운 뒤 감속해 멈춘다.
export const useRouletteStrip = itemCount => {
  const $ = _c(35);
  const isReducedMotion = useReducedMotion();
  const x = useMotionValue(0);
  const viewportRef = useRef(null);
  const [landedIndex, setLandedIndex] = useState(null);
  const controlsRef = useRef(null);
  const timerRef = useRef(null);
  const pendingLandedRef = useRef(null);
  const startedAtRef = useRef(0);
  const isLooping = !isReducedMotion && itemCount > 0;
  const cycleWidth = Math.max(1, itemCount) * ITEM_PITCH;
  const copyCount = Math.ceil((RAMP_DISTANCE + 3 * cycleWidth + MIN_LANDING_DISTANCE + MAX_VIEWPORT_WIDTH) / cycleWidth) + 1;
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      pendingLandedRef.current = null;
      controlsRef.current?.stop();
      controlsRef.current = null;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const halt = t0;
  let t1;
  if ($[1] !== cycleWidth || $[2] !== x) {
    t1 = speed => {
      const from = x.get() % cycleWidth;
      x.set(from);
      controlsRef.current = animate(x, [from, from - cycleWidth], {
        duration: cycleWidth / speed,
        ease: "linear",
        repeat: Infinity
      });
    };
    $[1] = cycleWidth;
    $[2] = x;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const loopFrom = t1;
  let t2;
  if ($[4] !== isLooping || $[5] !== loopFrom || $[6] !== x) {
    t2 = () => {
      halt();
      setLandedIndex(null);
      if (isLooping) {
        loopFrom(IDLE_SPEED_PX_PER_SECOND);
      } else {
        x.set(0);
      }
    };
    $[4] = isLooping;
    $[5] = loopFrom;
    $[6] = x;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const reset = t2;
  let t3;
  if ($[8] !== isLooping || $[9] !== loopFrom || $[10] !== x) {
    t3 = () => {
      if (isLooping) {
        loopFrom(IDLE_SPEED_PX_PER_SECOND);
      } else {
        x.set(0);
      }
    };
    $[8] = isLooping;
    $[9] = loopFrom;
    $[10] = x;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const idle = useEffectEvent(t3);
  let t4;
  if ($[12] !== idle) {
    t4 = () => {
      idle();
      return () => {
        const onLanded = pendingLandedRef.current;
        halt();
        setLandedIndex(null);
        onLanded?.();
      };
    };
    $[12] = idle;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== cycleWidth || $[15] !== isLooping) {
    t5 = [isLooping, cycleWidth];
    $[14] = cycleWidth;
    $[15] = isLooping;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  useEffect(t4, t5);
  let t6;
  if ($[17] !== cycleWidth || $[18] !== isLooping || $[19] !== loopFrom || $[20] !== x) {
    t6 = () => {
      halt();
      setLandedIndex(null);
      startedAtRef.current = Date.now();
      viewportRef.current?.scrollTo({
        left: 0
      });
      if (!isLooping) {
        return;
      }
      const from_0 = x.get() % cycleWidth;
      x.set(from_0);
      controlsRef.current = animate(x, from_0 - RAMP_DISTANCE, {
        duration: RAMP_SECONDS,
        ease: _temp,
        onComplete: () => loopFrom(SPIN_SPEED_PX_PER_SECOND)
      });
    };
    $[17] = cycleWidth;
    $[18] = isLooping;
    $[19] = loopFrom;
    $[20] = x;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  const start = t6;
  let t7;
  if ($[22] !== isLooping || $[23] !== itemCount || $[24] !== reset || $[25] !== x) {
    t7 = (landingIndex, onLanded_0) => {
      if (!isLooping) {
        reset();
        onLanded_0();
        return;
      }
      pendingLandedRef.current = onLanded_0;
      const remainingSpinMs = Math.max(0, MIN_SPIN_MS - (Date.now() - startedAtRef.current));
      const hold = stripIndex => {
        setLandedIndex(stripIndex);
        timerRef.current = setTimeout(() => {
          pendingLandedRef.current = null;
          onLanded_0();
          reset();
        }, LANDING_HOLD_MS);
      };
      timerRef.current = setTimeout(() => {
        controlsRef.current?.stop();
        if (landingIndex < 0) {
          hold(null);
          return;
        }
        const currentX = x.get();
        const viewportWidth = viewportRef.current?.clientWidth ?? 0;
        const centerOffset = viewportWidth / 2 - STRIP_PADDING - ITEM_WIDTH / 2;
        let stripIndex_0 = landingIndex;
        while (centerOffset - stripIndex_0 * ITEM_PITCH > currentX - MIN_LANDING_DISTANCE) {
          stripIndex_0 = stripIndex_0 + itemCount;
          stripIndex_0;
        }
        const targetX = centerOffset - stripIndex_0 * ITEM_PITCH;
        controlsRef.current = animate(x, targetX, {
          duration: 3 * (currentX - targetX) / SPIN_SPEED_PX_PER_SECOND,
          ease: EASE_OUT_CUBIC,
          onComplete: () => hold(stripIndex_0)
        });
      }, remainingSpinMs);
    };
    $[22] = isLooping;
    $[23] = itemCount;
    $[24] = reset;
    $[25] = x;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  const land = t7;
  let t8;
  if ($[27] !== copyCount || $[28] !== isLooping || $[29] !== land || $[30] !== landedIndex || $[31] !== reset || $[32] !== start || $[33] !== x) {
    t8 = {
      x,
      viewportRef,
      isLooping,
      copyCount,
      landedIndex,
      start,
      land,
      stop: reset
    };
    $[27] = copyCount;
    $[28] = isLooping;
    $[29] = land;
    $[30] = landedIndex;
    $[31] = reset;
    $[32] = start;
    $[33] = x;
    $[34] = t8;
  } else {
    t8 = $[34];
  }
  return t8;
};
function _temp(progress) {
  return progress ** 3;
}